import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/lib/hooks/use-toast';
import { customerService } from '@/features/customers/services/customer-service';

/**
 * Alta de cliente sin salir de la cotización.
 *
 * **Por qué existe.** El selector de "Para quién es" solo listaba clientes ya
 * cargados. Con un cliente nuevo había que abandonar la cotización, ir a
 * Clientes, darlo de alta y volver a empezar — y en la práctica eso termina en
 * que nadie lo carga y se cotiza como "importación propia", que borra a quién se
 * le cotizó.
 *
 * **Por qué no son campos sueltos en la cotización.** Es la tentación obvia y es
 * un error: en tres meses hay "Importadora del Sur", "IMPORTADORA DEL SUR" e
 * "Imp. del Sur SRL" con tres NIT tipeados distinto, y ninguna forma de saber
 * cuánto se le cotizó a ese cliente en el año. El cliente es una entidad, no un
 * texto de la cotización. Acá se crea de verdad, y queda para la próxima.
 *
 * Pide lo mínimo que pidió ABCD: nombre, NIT, teléfono y correo. Lo demás
 * —dirección, rubro, segmento— se completa después en Clientes si hace falta.
 */

/** Valor del selector que abre este diálogo. No es un id de cliente. */
export const NUEVO_CLIENTE = '__nuevo_cliente__';

export function NuevoClienteDialog({
  open,
  onOpenChange,
  onCreado,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** El cliente recién creado, para seleccionarlo y refrescar la lista. */
  onCreado: (cliente: { id: string; name: string }) => void;
}) {
  const { toast } = useToast();
  const [nombre, setNombre] = useState('');
  const [nit, setNit] = useState('');
  const [telefono, setTelefono] = useState('');
  const [correo, setCorreo] = useState('');
  const [guardando, setGuardando] = useState(false);

  const limpiar = () => {
    setNombre('');
    setNit('');
    setTelefono('');
    setCorreo('');
  };

  const guardar = async () => {
    const name = nombre.trim();
    if (!name) {
      toast({ title: 'Falta el nombre del cliente', variant: 'destructive' });
      return;
    }

    setGuardando(true);
    try {
      const creado = await customerService.createCustomer({
        name,
        /*
          El código es obligatorio en el modelo y nadie lo tiene a mano cuando
          está cotizando. Se deriva del nombre —mayúsculas, sin espacios ni
          acentos— y se le pega la hora para que dos clientes parecidos no
          choquen. Se puede cambiar después desde Clientes.
        */
        code: derivarCodigo(name),
        taxId: nit.trim() || null,
        phone: telefono.trim() || null,
        email: correo.trim() || null,
      });
      toast({ title: `Cliente "${creado.name}" creado` });
      onCreado({ id: creado.id, name: creado.name });
      limpiar();
      onOpenChange(false);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        (e instanceof Error ? e.message : 'No se pudo crear el cliente.');
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Nuevo cliente</DialogTitle>
          <DialogDescription>
            Queda guardado en Clientes, así que la próxima vez lo buscás y listo.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="nc-nombre">Nombre o razón social *</Label>
            <Input
              id="nc-nombre"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Importadora del Sur SRL"
              autoFocus
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="nc-nit">NIT</Label>
            <Input
              id="nc-nit"
              value={nit}
              onChange={(e) => setNit(e.target.value)}
              placeholder="1234567019"
            />
            {/* Se dice para qué sirve: no es un campo administrativo más. */}
            <p className="text-[11px] text-muted-foreground">
              Va en el PDF de la cotización. Sin NIT no se puede facturar.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="nc-tel">Teléfono</Label>
              <Input
                id="nc-tel"
                value={telefono}
                onChange={(e) => setTelefono(e.target.value)}
                placeholder="+591 3 3456789"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="nc-mail">Correo</Label>
              <Input
                id="nc-mail"
                type="email"
                value={correo}
                onChange={(e) => setCorreo(e.target.value)}
                placeholder="compras@cliente.com"
              />
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Con el correo cargado se le puede mandar el enlace de seguimiento directo, sin copiarlo
            a mano.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={guardando}>
            Cancelar
          </Button>
          <Button onClick={guardar} disabled={guardando || !nombre.trim()}>
            {guardando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Crear y usar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Un código a partir del nombre.
 *
 * Sin acentos ni caracteres raros, en mayúsculas y acotado, con un sufijo de
 * tiempo para que "Importadora del Sur" e "Importadora del Sud" no colisionen.
 */
function derivarCodigo(nombre: string): string {
  const base = nombre
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .toUpperCase()
    .slice(0, 10);
  const sufijo = Date.now().toString(36).slice(-4).toUpperCase();
  return `${base || 'CLI'}-${sufijo}`;
}
